/**
 * One settled query, one short stream (`KLQ-20260905-001` §5a, client rules).
 *
 * - **300 ms of stillness** before the server is asked — the `FeedPanel.tsx` idiom.
 * - **A new settled query aborts the previous stream** through its `AbortController`: the
 *   request is cancelled, the reader stops, and the server's disconnect watch skips the vector
 *   statement (phase 4, Notes item 4).
 * - **An event whose `q` is not the current query is dropped**, before it reaches state. The
 *   abort already stops almost every such event; the check is what holds the promise for the one
 *   already parsed when the abort landed. The state also carries the query it belongs to, and
 *   the view is derived against the current query, so an answer for `rezid` can never be drawn
 *   under `rezidencia`.
 *
 * Nothing here is cached or kept live. A stream opens per settled query and closes on `done`;
 * typing the same query again asks again, which is the same work as the first time (§7).
 */

import { useEffect, useState } from 'react';

import { ApiError } from '@/api/client';
import type { Problem } from '@/api/client';

import { normaliseQuery, streamSearch } from './api';
import { accept, IDLE, waitingFor } from './streamState';
import type { SearchStreamState } from './streamState';

// `model.ts` and the screens read the state's shape from here; it lives in `./streamState`.
export type { SearchPhase, SearchStreamState } from './streamState';

/**
 * How still the box has to be before the server is asked.
 *
 * TWIN: `SETTLE_MS` in `features/comments/FeedPanel.tsx` and
 * `features/activity/ActivitySearchBox.tsx` — one pause for every search box.
 */
const SETTLE_MS = 300;

/** A failure that never produced a problem body — offline, a dropped connection. */
function problemOf(error: unknown): Problem {
  if (error instanceof ApiError) return error.problem;
  return { type: 'about:blank', title: 'Request failed', status: 0, code: 'internal_error' };
}

/**
 * `value`, once it has stopped changing for `SETTLE_MS`. The value it was mounted with is
 * settled from the start: a results page opened on `?q=…` asks at once, not 300 ms later.
 */
export function useSettled(value: string): string {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    // The last keystroke wins: every earlier timer is cleared by the cleanup, so a burst of
    // typing schedules many and fires one.
    const timer = window.setTimeout(() => {
      setSettled(value);
    }, SETTLE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [value]);
  return settled;
}

export interface SearchStream {
  /** The normalised query the answer is for — what "show all" carries to the results page. */
  readonly settled: string;
  readonly state: SearchStreamState;
}

export function useSearchStream(input: string): SearchStream {
  const settled = useSettled(normaliseQuery(input));
  const [held, setHeld] = useState<SearchStreamState>(IDLE);

  useEffect(() => {
    if (settled === '') return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    // Every write goes through here: an aborted stream writes nothing, and a write for another
    // query is refused even if it slipped past the abort.
    const update = (next: (state: SearchStreamState) => SearchStreamState): void => {
      if (signal.aborted) return;
      setHeld((state) => next(state.q === settled ? state : waitingFor(settled)));
    };

    void (async () => {
      try {
        for await (const event of streamSearch(settled, signal)) {
          // A superseded query's event is dropped in `accept` and never reaches state (§5a).
          update((state) => accept(settled, state, event));
        }
      } catch (error) {
        update((state) => ({ ...state, phase: 'done', problem: problemOf(error) }));
        return;
      }
      // A stream that closed without `done` — a proxy timeout, a server restart. Nothing more
      // is coming, so the pending rows must go rather than shimmer for ever.
      update((state) => (state.phase === 'done' ? state : { ...state, phase: 'done' }));
    })();

    return () => {
      controller.abort();
    };
  }, [settled]);

  // Derived, not stored: whatever the held answer is, it is drawn only under its own query.
  let state: SearchStreamState;
  if (settled === '') state = IDLE;
  else if (held.q === settled) state = held;
  else state = waitingFor(settled);

  return { settled, state };
}
